
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, FileText } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useSceneManager } from './useSceneManager';
import SceneList from './SceneList';
import SceneForm from './SceneForm';
import { ElfScene } from "@/components/tools/scene-generator/types";

const SceneIdeasManager = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  
  const {
    scenes,
    isLoading,
    isSubmitting,
    currentScene,
    setCurrentScene,
    handleSubmit,
    handleDelete,
    resetForm,
    addItemField,
    handleItemChange,
    removeItemField
  } = useSceneManager();

  const handleEdit = (scene: ElfScene) => {
    setCurrentScene(scene);
    setIsEditing(true);
  };

  const handleReset = () => {
    resetForm();
    setIsEditing(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await handleSubmit(isEditing);
    if (success) {
      setIsEditing(false);
      toast({
        title: isEditing ? "Scene Updated" : "Scene Created",
        description: `Successfully ${isEditing ? 'updated' : 'created'} the elf scene.`,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Holiday Elf Scenes</h1>
          <p className="text-gray-500">
            Manage scene ideas for the Elf Scene Generator tool
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/tools/scene-importer")}
            className="flex items-center gap-1"
          >
            <FileText size={16} />
            Import Scenes
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setIsEditing(false);
            }}
            className="bg-elfGreen hover:bg-elfGreen-dark flex items-center gap-1"
          >
            <PlusCircle size={16} />
            Add New Scene
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="pt-6">
              <SceneList 
                scenes={scenes}
                isLoading={isLoading}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-xl font-semibold mb-4">
                {isEditing ? 'Edit Scene' : 'Create New Scene'}
              </h2>
              <SceneForm
                currentScene={currentScene}
                isEditing={isEditing}
                isSubmitting={isSubmitting}
                onSubmit={onSubmit}
                onReset={handleReset}
                setCurrentScene={setCurrentScene}
                addItemField={addItemField}
                handleItemChange={handleItemChange}
                removeItemField={removeItemField}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SceneIdeasManager;
